// Ubicación en el repo: src/components/ForceDeletePatientModal.jsx
// Modal "Forzar eliminación definitiva" — pedido de Nicolás (25-ago-2026):
// tiene fichas de PRUEBA en la papelera que no puede eliminar porque
// alguna vez tuvieron historial clínico asociado, y `patient_has_history()`
// (migración 0005) bloquea eso a propósito para cualquier ficha real. Esto
// es la salida deliberada — Admin, contraseña reingresada + motivo
// obligatorio, todo queda registrado en audit_log (migración 0047).
//
// Por qué reingresar la contraseña: es una acción irreversible y saltarse
// una protección legal (retención de fichas clínicas), no basta un
// window.confirm(). Se verifica contra un Edge Function aislado
// (verify-current-password) que no toca la sesión 2FA activa — ver
// lib/auth.js#verifyCurrentPassword.
import { useState } from 'react';
import { verifyCurrentPassword } from '../lib/auth';
import { forceDeletePatientWithReason } from '../lib/patients';
import { formatPatientName } from '../lib/format';

export default function ForceDeletePatientModal({ patient, onClose, onDeleted }) {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const nombre = formatPatientName(patient);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Debes reingresar tu contraseña.');
      return;
    }
    if (!reason.trim()) {
      setError('Debes indicar un motivo.');
      return;
    }

    setSaving(true);
    try {
      const passwordOk = await verifyCurrentPassword(password);
      if (!passwordOk) {
        setError('Contraseña incorrecta.');
        setSaving(false);
        return;
      }

      const confirmed = window.confirm(
        `Estás a punto de eliminar DEFINITIVAMENTE la ficha de ${nombre}, junto con todo su historial clínico (citas, exámenes, recetas, audífonos y ventas). Esto no se puede deshacer. ¿Continuar?`
      );
      if (!confirmed) {
        setSaving(false);
        return;
      }

      await forceDeletePatientWithReason(patient.id, reason.trim());
      onDeleted();
    } catch (err) {
      setError(err.message || 'No se pudo forzar la eliminación definitiva.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h2>Forzar eliminación definitiva</h2>
        <p className="field-hint" style={{ color: 'var(--danger, #c0392b)' }}>
          Ficha de <strong>{nombre}</strong>. Esta ficha tiene historial clínico asociado — normalmente eso la
          protege de un borrado definitivo. Esta acción pasa por encima de esa protección a propósito, queda
          registrada con tu usuario y el motivo que indiques, y no se puede deshacer.
        </p>
        <form onSubmit={handleSubmit} className="patient-form">
          <label className="field">
            <span>Tu contraseña *</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
            />
          </label>
          <label className="field">
            <span>Motivo *</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Ej: Paciente de prueba, datos ficticios, sin relación con un paciente real."
            />
          </label>
          {error && <p className="auth-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="button-primary" disabled={saving}>
              {saving ? 'Verificando…' : 'Eliminar definitivamente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
